import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import {WaitlistDay} from '../../../enums';
import {ActivateHandle} from '../../../interfaces';
import WaitlistEntry, {WaitlistEntryFields} from '../../../models/waitlistEntryModel';
import {ResponseData} from '@dvrd/fetch';
import {capitalize, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import EntryData from './entryData';

interface Props {
    onSuccess: VoidFunction;
}

function EntryDataController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSuccess} = props;
    const [entry, setEntry] = useState<WaitlistEntry>(new WaitlistEntry());
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);

    function onActivate(entry?: WaitlistEntry) {
        if (entry) setEntry(entry);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setEntry(new WaitlistEntry());
        setLoading(false);
    }

    function onChange(key: keyof WaitlistEntryFields) {
        return function (value: any) {
            setEntry(entry.copy.setField(key, value));
        };
    }

    function onSelectDay(day: WaitlistDay) {
        return function () {
            const _entry = entry.copy;
            if (_entry.days.includes(day)) _entry.days.splice(_entry.days.indexOf(day), 1);
            else _entry.days.push(day);
            setEntry(_entry);
        };
    }

    function onSubmit() {
        if (!entry.days.length) {
            if (!entry.id) return onClose();
            else showDialog(
                'Er zijn geen dagen meer geselecteerd, de klant wordt van de wachtlijst verwijderd. Wil je doorgaan?',
                'Klant verwijderen', [{label: 'Nee', primary: false}, {label: 'Ja', onClick: onConfirmSubmit}]);
        } else onConfirmSubmit();
    }

    function onConfirmSubmit() {
        setLoading(true);
        if (!entry.id) onSave();
        else onUpdate();
    }

    function onSave() {
        entry.create(submitCallback('toevoegen', 'toegevoegd'));
    }

    function onUpdate() {
        entry.update(submitCallback('bijwerken', 'bijgewerkt'));
    }

    function submitCallback(action: string, actionDone: string) {
        return function (_: WaitlistEntry, success: boolean, data: ResponseData) {
            setLoading(false);
            if (success) {
                onSuccess();
                onClose();
                showSnackbar(`De klant is ${actionDone}`);
            } else
                showDialog(data.message ?? `Het ${action} van de klant is niet gelukt.`,
                    `${capitalize(action)} mislukt`);
        };
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <EntryData onClose={onClose} onChange={onChange} onSelectDay={onSelectDay} onSubmit={onSubmit}
                   entry={entry} active={active} loading={loading}/>
    );
}

EntryDataController.displayName = 'EntryDataController';

export default forwardRef<ActivateHandle, Props>(EntryDataController);